import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiContext } from '../../../../../Contexts/ApiProvider';
import { FaArrowLeft } from 'react-icons/fa';
import { useJwt } from '../../../../../Contexts/JwtProvider';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import MainFooter from '../../../../Partials/Footer/MainFooter';
import MainHeader from '../../../../Partials/Header/MainHeader';
import { Country } from '../../../../../types/enums';

interface FormValues {
  name: string;
  numberOfTeams: number;
  country: string;
  numberOfRounds: number;
}

const AdminRegisterLeaguePage: React.FC = () => {
  const { role } = useJwt();
  const apiContext = useContext(ApiContext);
  const navigate = useNavigate();

  useEffect(() => {
    if (role !== 'admin') {
      navigate('/notauthorized');
    }
  }, [role, navigate]);

  if (!apiContext) {
    throw new Error('ApiContext must be used within an ApiProvider');
  }

  const { registerLeagueAsync } = apiContext;

  const [resultMessage, setResultMessage] = useState<string | null>(null);

  // Get numeric values of Country enum and convert to strings
  const countryValues = Object.values(Country)
    .filter(value => typeof value === 'number')
    .map(value => value.toString());

  const validationSchema = Yup.object({
    name: Yup.string().min(2, 'Must be at least 2 characters').required('Required'),
    numberOfTeams: Yup.number().min(1, 'Must be at least 1').required('Required'),
    country: Yup.string().oneOf(countryValues, 'Invalid country').required('Required'),
    numberOfRounds: Yup.number().min(1, 'Must be at least 1').required('Required'),
  });

  const formik = useFormik<FormValues>({
    initialValues: {
      name: '',
      numberOfTeams: 0,
      country: '',
      numberOfRounds: 0,
    },
    validationSchema: validationSchema,
    onSubmit: async values => {
      const request = {
        ...values,
        country: parseInt(values.country, 10) as Country,
      };
      const success = await registerLeagueAsync(request);
      if (success) {
        setResultMessage('League registration was successful');
        formik.resetForm();
      } else {
        setResultMessage('League registration failed');
      }
    },
  });

  return (
    <div className="flex flex-col min-h-screen">
      <MainHeader />
      <div className="flex-1 container mx-auto px-4 pt-20">
        <div className="flex items-center mb-6">
          <button className="text-primary hover:text-primary-dark" onClick={() => navigate(-1)}>
            <FaArrowLeft size={24} />
          </button>
          <h1 className="text-3xl font-bold text-primary text-center flex-grow">Register League</h1>
        </div>
        <form onSubmit={formik.handleSubmit} className="bg-white shadow-md rounded-lg p-6 w-full max-w-lg mx-auto">
          {/* League Name */}
          <div className="mb-4">
            <label htmlFor="name" className="block text-sm font-medium text-gray-700">
              League Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              placeholder="Enter league name"
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary focus:border-primary sm:text-sm"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.name && formik.errors.name && (
              <div className="text-red-500 text-sm mt-1">{formik.errors.name}</div>
            )}
          </div>
          {/* Number of Teams */}
          <div className="mb-4">
            <label htmlFor="numberOfTeams" className="block text-sm font-medium text-gray-700">
              Number of Teams <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="numberOfTeams"
              placeholder="Enter number of teams"
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary focus:border-primary sm:text-sm"
              value={formik.values.numberOfTeams}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.numberOfTeams && formik.errors.numberOfTeams && (
              <div className="text-red-500 text-sm mt-1">{formik.errors.numberOfTeams}</div>
            )}
          </div>
          {/* Country */}
          <div className="mb-4">
            <label htmlFor="country" className="block text-sm font-medium text-gray-700">
              Country <span className="text-red-500">*</span>
            </label>
            <select
              name="country"
              className="mt-1 block w-full border border-gray-300 bg-white rounded-md shadow-sm py-2 px-3 focus:ring-primary focus:border-primary sm:text-sm"
              value={formik.values.country}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}>
              <option value="" label="Select country" />
              {Object.keys(Country)
                .filter(key => isNaN(Number(key)))
                .map(key => {
                  const value = Country[key as keyof typeof Country] as number;
                  return (
                    <option key={key} value={value.toString()}>
                      {key.replace(/([A-Z])/g, ' $1').trim()}
                    </option>
                  );
                })}
            </select>
            {formik.touched.country && formik.errors.country && (
              <div className="text-red-500 text-sm mt-1">{formik.errors.country}</div>
            )}
          </div>
          {/* Number of Rounds */}
          <div className="mb-6">
            <label htmlFor="numberOfRounds" className="block text-sm font-medium text-gray-700">
              Number of Rounds <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="numberOfRounds"
              placeholder="Enter number of rounds"
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary focus:border-primary sm:text-sm"
              value={formik.values.numberOfRounds}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.numberOfRounds && formik.errors.numberOfRounds && (
              <div className="text-red-500 text-sm mt-1">{formik.errors.numberOfRounds}</div>
            )}
          </div>
          {/* Submit Button */}
          <button
            type="submit"
            className="w-full bg-primary text-white py-2 px-4 rounded-md hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary">
            Register League
          </button>
        </form>
        {/* Result Message */}
        {resultMessage && <div className="mt-6 text-center text-lg font-semibold text-primary">{resultMessage}</div>}
      </div>
      <MainFooter />
    </div>
  );
};

export default AdminRegisterLeaguePage;
